import {
  Apps as AppIcon,
  LocationCity as CityIcon,
  Groups as EveryoneIcon,
  Person as UserIcon,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getWhiteLabel } from "../../actions/cities";
import { isSuperAdmin } from "../../utils/utils";
import { NotificationsTargetApp } from "./NotificationsTargetApp";
import { NotificationsTargetCity } from "./NotificationsTargetCity";
import { NotificationsTargetUser } from "./NotificationsTargetUser";

export type TargetStrategy = "everyone" | "cities" | "users" | "apps";

export interface TargetData {
  targetType: string | null;
  targetValue: string | null;
  targetUserIds: string[];
  selectedApp: { label: string; id: number } | null;
  selectedCities: { label: string; id: number }[];
  // biome-ignore lint/suspicious/noExplicitAny: V1 legacy code without typed Redux store
  selectedUsers: any[];
  strategy: TargetStrategy;
}

interface Props {
  targetData: TargetData;
  setTargetData: (data: TargetData) => void;
}

export const NotificationsTarget = ({ targetData, setTargetData }: Props) => {
  const dispatch = useDispatch();
  // biome-ignore lint/suspicious/noExplicitAny: V1 legacy code without typed Redux store
  const adminDetails = useSelector((state: any) => state.auth.adminDetails);
  // biome-ignore lint/suspicious/noExplicitAny: V1 legacy code without typed Redux store
  const whitelabels = useSelector((state: any) => state.cities.whiteLabelList);
  const isSuper = isSuperAdmin(adminDetails);

  useEffect(() => {
    dispatch(getWhiteLabel());
  }, [dispatch]);

  const currentWhitelabel = useMemo(() => {
    if (isSuper) return null;
    if (adminDetails?.whiteLabelId) {
      const wl = whitelabels?.find(
        // biome-ignore lint/suspicious/noExplicitAny: V1 legacy code without typed Redux store
        (w: any) => w.id === adminDetails.whiteLabelId
      );
      if (wl) return wl;
      return {
        id: adminDetails.whiteLabelId,
        name: adminDetails.whiteLabelCode || "Your App",
      };
    }
    return null;
  }, [isSuper, adminDetails, whitelabels]);

  useEffect(() => {
    if (
      targetData.strategy === "everyone" &&
      !currentWhitelabel &&
      whitelabels !== undefined
    ) {
      setTargetData({
        ...targetData,
        strategy: isSuper ? "apps" : "cities",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWhitelabel, whitelabels, isSuper, setTargetData]);

  const handleStrategyChange = (newStrategy: TargetStrategy) => {
    setTargetData({
      ...targetData,
      strategy: newStrategy,
      selectedApp: null,
      selectedCities: [],
      selectedUsers: [],
    });
  };

  const handleAppSelect = (app: { label: string; id: number } | null) => {
    setTargetData({
      ...targetData,
      selectedApp: app,
      selectedCities: [],
      selectedUsers: [],
    });
  };

  const handleCitySelect = (cities: { label: string; id: number }[]) => {
    setTargetData({
      ...targetData,
      selectedCities: cities,
      selectedApp: null,
      selectedUsers: [],
    });
  };

  // biome-ignore lint/suspicious/noExplicitAny: V1 legacy code without typed Redux store
  const handleUserSelect = (users: any[]) => {
    setTargetData({
      ...targetData,
      selectedUsers: users,
      selectedApp: null,
      selectedCities: [],
    });
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Who should receive this?</Typography>

      <FormControl component="fieldset">
        <RadioGroup
          value={targetData.strategy}
          onChange={(e) =>
            handleStrategyChange(e.target.value as TargetStrategy)
          }
        >
          <Stack spacing={1.5}>
            {/* Everyone option - only for non-super admins with a single whitelabel */}
            {currentWhitelabel && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  cursor: "pointer",
                  borderColor:
                    targetData.strategy === "everyone"
                      ? "primary.main"
                      : "divider",
                  borderWidth: targetData.strategy === "everyone" ? 2 : 1,
                  bgcolor:
                    targetData.strategy === "everyone"
                      ? "primary.50"
                      : "transparent",
                  transition: "all 0.2s",
                  "&:hover": { borderColor: "primary.light" },
                }}
                onClick={() => handleStrategyChange("everyone")}
              >
                <FormControlLabel
                  value="everyone"
                  control={<Radio />}
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <EveryoneIcon
                        color={
                          targetData.strategy === "everyone"
                            ? "primary"
                            : "action"
                        }
                      />
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium">
                          Everyone
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          All users of {currentWhitelabel.name}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{ m: 0, width: "100%" }}
                />
              </Paper>
            )}

            {/* Specific cities option */}
            <Paper
              variant="outlined"
              sx={{
                cursor: "pointer",
                borderColor:
                  targetData.strategy === "cities" ? "primary.main" : "divider",
                borderWidth: targetData.strategy === "cities" ? 2 : 1,
                bgcolor:
                  targetData.strategy === "cities"
                    ? "primary.50"
                    : "transparent",
                transition: "all 0.2s",
                "&:hover": { borderColor: "primary.light" },
                overflow: "hidden",
              }}
            >
              <Box sx={{ p: 2 }} onClick={() => handleStrategyChange("cities")}>
                <FormControlLabel
                  value="cities"
                  control={<Radio />}
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <CityIcon
                        color={
                          targetData.strategy === "cities"
                            ? "primary"
                            : "action"
                        }
                      />
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium">
                          Specific cities
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Target users in selected cities only
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{ m: 0, width: "100%" }}
                />
              </Box>
              <Collapse in={targetData.strategy === "cities"}>
                <Box
                  sx={{
                    px: 2,
                    pb: 2,
                    pt: 1,
                    borderTop: 1,
                    borderColor: "divider",
                    bgcolor: "grey.50",
                  }}
                >
                  <NotificationsTargetCity onSelect={handleCitySelect} />
                </Box>
              </Collapse>
            </Paper>

            {/* Specific users option */}
            <Paper
              variant="outlined"
              sx={{
                cursor: "pointer",
                borderColor:
                  targetData.strategy === "users" ? "primary.main" : "divider",
                borderWidth: targetData.strategy === "users" ? 2 : 1,
                bgcolor:
                  targetData.strategy === "users"
                    ? "primary.50"
                    : "transparent",
                transition: "all 0.2s",
                "&:hover": { borderColor: "primary.light" },
                overflow: "hidden",
              }}
            >
              <Box sx={{ p: 2 }} onClick={() => handleStrategyChange("users")}>
                <FormControlLabel
                  value="users"
                  control={<Radio />}
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <UserIcon
                        color={
                          targetData.strategy === "users" ? "primary" : "action"
                        }
                      />
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium">
                          Specific users
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Search and select individual users
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{ m: 0, width: "100%" }}
                />
              </Box>
              <Collapse in={targetData.strategy === "users"}>
                <Box
                  sx={{
                    px: 2,
                    pb: 2,
                    pt: 1,
                    borderTop: 1,
                    borderColor: "divider",
                    bgcolor: "grey.50",
                  }}
                >
                  <NotificationsTargetUser onSelect={handleUserSelect} />
                </Box>
              </Collapse>
            </Paper>

            {/* Super admin: specific app option */}
            {isSuper && (
              <Paper
                variant="outlined"
                sx={{
                  cursor: "pointer",
                  borderColor:
                    targetData.strategy === "apps" ? "primary.main" : "divider",
                  borderWidth: targetData.strategy === "apps" ? 2 : 1,
                  bgcolor:
                    targetData.strategy === "apps"
                      ? "primary.50"
                      : "transparent",
                  transition: "all 0.2s",
                  "&:hover": { borderColor: "primary.light" },
                  overflow: "hidden",
                }}
              >
                <Box sx={{ p: 2 }} onClick={() => handleStrategyChange("apps")}>
                  <FormControlLabel
                    value="apps"
                    control={<Radio />}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <AppIcon
                          color={
                            targetData.strategy === "apps"
                              ? "primary"
                              : "action"
                          }
                        />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">
                            Specific app
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Target all users of a specific whitelabel app
                          </Typography>
                        </Box>
                      </Box>
                    }
                    sx={{ m: 0, width: "100%" }}
                  />
                </Box>
                <Collapse in={targetData.strategy === "apps"}>
                  <Box
                    sx={{
                      px: 2,
                      pb: 2,
                      pt: 1,
                      borderTop: 1,
                      borderColor: "divider",
                      bgcolor: "grey.50",
                    }}
                  >
                    <NotificationsTargetApp onSelect={handleAppSelect} />
                  </Box>
                </Collapse>
              </Paper>
            )}
          </Stack>
        </RadioGroup>
      </FormControl>
    </Stack>
  );
};
